// The workflow marketplace: published portable workflows, filtered by category or searched.
import { MagnifyingGlass } from "@phosphor-icons/react";
import clsx from "clsx";
import { useState } from "react";
import { Link, useSearchParams } from "react-router";

import { useWorkflows } from "../api/hooks";
import { useTitle } from "../ui/hooks";
import { Failed, Loading, PageHero } from "../ui/Section";
import { WorkflowCard } from "./workflows/WorkflowCard";

export function Workflows() {
  useTitle("Workflow marketplace");
  const [params, setParams] = useSearchParams();
  const category = params.get("category") ?? undefined;
  const [q, setQ] = useState(params.get("q") ?? "");
  const list = useWorkflows(category, params.get("q") ?? undefined);

  const set = (key: string, value: string | undefined) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    setParams(next, { replace: true });
  };

  const total = list.data?.categories.reduce((n, c) => n + c.count, 0) ?? 0;
  return (
    <>
      <PageHero
        kicker="§ Workflow marketplace"
        title={<>Automations that have been tried.</>}
        aside={
          <div className="card p-5 text-[15px] text-ink2">
            Every workflow here is a <Link to="/portable-workflows" className="link">portable workflow</Link>: open it, read every step, prompt and guard, see who has to approve what, then download the exact
            version you trust.
          </div>
        }
      >
        Tested AI workflows, published with their versions and changelogs. Watch each one run, open any step, and take it home.
      </PageHero>
      <section className="wrap py-10">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap gap-2" role="group" aria-label="Category">
            <button type="button" onClick={() => set("category", undefined)} className={clsx("chip !px-3.5 !py-1.5 !text-[12px]", !category && "!border-ink !bg-ink !text-paper")}>
              All {total ? `· ${total}` : ""}
            </button>
            {list.data?.categories.map((c) => (
              <button
                type="button"
                key={c.name}
                onClick={() => set("category", c.name)}
                className={clsx("chip !px-3.5 !py-1.5 !text-[12px]", category === c.name && "!border-ink !bg-ink !text-paper")}
              >
                {c.name} · {c.count}
              </button>
            ))}
          </div>
          <form
            role="search"
            onSubmit={(e) => {
              e.preventDefault();
              set("q", q.trim() || undefined);
            }}
            className="relative w-full lg:w-80"
          >
            <MagnifyingGlass className="pointer-events-none absolute start-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-ink3" aria-hidden />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search workflows" aria-label="Search workflows" className="field !rounded-full ps-10" />
          </form>
        </div>
        {list.isPending ? <Loading label="Loading workflows" /> : null}
        {list.isError ? <Failed what="the workflows" retry={() => void list.refetch()} /> : null}
        {list.data ? (
          list.data.workflows.length === 0 ? (
            <p className="lede py-16">No workflow matches that yet. Try another word, or publish one.</p>
          ) : (
            <div className="mt-8 grid gap-5 md:grid-cols-2 xl:grid-cols-3">
              {list.data.workflows.map((w) => (
                <WorkflowCard key={w.slug} workflow={w} />
              ))}
            </div>
          )
        ) : null}
      </section>
      <section className="wrap">
        <div className="grid gap-8 rounded-3xl border border-ink p-8 sm:p-10 lg:grid-cols-[1.4fr_1fr] lg:items-center">
          <div>
            <p className="kicker">Publish yours</p>
            <p className="h2 mt-3">Your team already solved something. Share how.</p>
            <p className="mt-4 max-w-xl text-[15.5px] text-ink2">
              Organisations that publish tested workflows set the standard for everyone else. Tell us what you automate and we will help you get it listed, with your name on it.
            </p>
          </div>
          <div className="flex flex-wrap gap-3 lg:justify-end">
            <Link to="/charter?interest=publish#sign" className="btn-ink">
              I want to publish
            </Link>
            <Link to="/tools/validator" className="btn-line">
              Validate a workflow
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
